import { useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { loginJwt } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();

  const pkg = params.get("pkg");
  const destination =
    loc.state?.from ||
    (pkg ? `/dashboard/book?pkg=${pkg}` : "/dashboard");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await loginJwt(form.email, form.password);
      toast.success("Welcome back.");
      // Preserve the original destination (including ?pkg=...) so offer selection survives login.
      nav(u.role === "admin" ? "/admin" : destination, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + destination;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="login-page" className="font-body max-w-md mx-auto px-6 md:px-10 pt-32 pb-20 sm:pt-40">
      <span className="rc-eyebrow block text-center">Client Portal</span>
      <h1 className="font-display text-4xl sm:text-5xl font-semibold text-center mt-4" style={{ letterSpacing: "-0.03em" }}>Sign in.</h1>
      <p className="mt-4 text-sm text-[color:var(--rc-text-muted)] text-center">Return to your appointments, intake, and reflection.</p>

      <button data-testid="google-login-btn" onClick={googleLogin} className="mt-10 w-full rc-btn rc-btn-outline-new">
        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"/><path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="currentColor" d="M5.84 14.11c-.22-.66-.35-1.36-.35-2.11s.13-1.45.35-2.11V7.05H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.95l3.66-2.84z"/><path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84c.87-2.6 3.3-4.51 6.16-4.51z"/></svg>
        Continue with Google
      </button>

      <div className="my-8 flex items-center gap-3 text-xs text-[color:var(--rc-text-dim)] uppercase tracking-[0.12em]">
        <div className="h-px flex-1 bg-[color:var(--rc-divider)]" /> or <div className="h-px flex-1 bg-[color:var(--rc-divider)]" />
      </div>

      <form onSubmit={submit} data-testid="login-form" className="space-y-4">
        <div>
          <label htmlFor="login-email" className="block mb-2 text-xs font-semibold uppercase tracking-[0.1em] text-[color:var(--rc-text-muted)]">Email</label>
          <input id="login-email" data-testid="login-email" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-3" />
        </div>
        <div>
          <label htmlFor="login-password" className="block mb-2 text-xs font-semibold uppercase tracking-[0.1em] text-[color:var(--rc-text-muted)]">Password</label>
          <input id="login-password" data-testid="login-password" type="password" required value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full px-4 py-3" />
        </div>
        <button data-testid="login-submit" type="submit" disabled={loading} className="w-full rc-btn rc-btn-blue disabled:opacity-50">
          {loading ? "Signing in…" : <>Sign In <ArrowRight className="w-4 h-4" /></>}
        </button>
      </form>

      <p className="mt-8 text-sm text-[color:var(--rc-text-muted)] text-center">
        New here? <Link to={pkg ? `/signup?pkg=${pkg}` : "/signup"} state={loc.state} data-testid="link-to-signup" className="text-[#2d7cf6] underline-editorial font-medium">Create an account</Link>
      </p>
    </div>
  );
}
